import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { Observable, finalize, take, tap, throwError } from 'rxjs';
import { ApiErrorBody } from '@core/models/api-error.model';
import { ApiErrorService } from '@core/services/api-error.service';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import {
  AVATAR_ACCEPTED_MIME_TYPES,
  AVATAR_MAX_BYTES,
  AVATAR_REJECTION_MESSAGE_KEY,
  AvatarRejectionReason,
} from '../models/avatar-upload.model';
import {
  PASSWORD_FIELD_KEYS,
  PasswordChangeRequest,
  PasswordChangeResult,
} from '../models/password-change.model';
import { UserAvatar, UserProfile } from '../models/user-profile.model';
import { MyAccountService } from './my-account.service';
import { MyAccountStateService } from './my-account-state.service';

/** Field the avatar upload reports its `422` under. */
const AVATAR_FIELD_KEYS: ReadonlySet<string> = new Set(['photo']);

@Injectable({ providedIn: 'root' })
export class MyAccountFacadeService {
  private readonly api = inject(MyAccountService);
  private readonly state = inject(MyAccountStateService);
  private readonly apiErrors = inject(ApiErrorService);
  private readonly appStateReset = inject(AppStateResetService);

  /**
   * Version being downloaded right now. The state only learns the version when
   * the blob lands, so without this two profile loads racing each other (the
   * header warming it while the account page reloads it) would each start the
   * same download.
   */
  private pendingAvatarVersion: string | null = null;

  constructor() {
    // The facade, not the state service: `pendingAvatarVersion` lives here.
    this.appStateReset.register(() => this.reset());
  }

  /** Profile of the authenticated user, or `null` before the first load returns. */
  readonly profile = this.state.profile;

  /** Object URL of the profile picture, or `null` when there is none to render. */
  readonly avatarUrl = this.state.avatarUrl;

  readonly vm = computed(() => ({
    profile: this.state.profile(),
    status: this.state.status(),
    isLoading: this.state.isLoading(),
    isLoaded: this.state.isLoaded(),
    error: this.state.error(),
    avatarUrl: this.state.avatarUrl(),
    isUploadingAvatar: this.state.isUploadingAvatar(),
    avatarUploadError: this.state.avatarUploadError(),
    isDownloadingAvatar: this.state.isDownloadingAvatar(),
    isRemovingAvatar: this.state.isRemovingAvatar(),
    avatarRemoveError: this.state.avatarRemoveError(),
    isSavingPassword: this.state.isSavingPassword(),
    passwordError: this.state.passwordError(),
    passwordFieldErrors: this.state.passwordFieldErrors(),
  }));

  /**
   * Loads the profile and keeps the state in sync. Returns the stream so the
   * page can drive feedback on success/error.
   */
  loadProfile(): Observable<UserProfile> {
    this.state.setLoading();

    return this.api.getProfile().pipe(
      take(1),
      tap({
        next: (profile) => {
          this.state.setProfile(profile);
          this.syncAvatar(profile.avatar);
        },
        error: (error: unknown) => this.state.setError(this.extractErrorMessage(error)),
      }),
    );
  }

  /**
   * Loads the profile once for callers that only consume it — the shell header
   * wants the picture on every dashboard route, not only after a visit to the
   * account page. A load already done (or in flight) is not repeated.
   */
  ensureProfile(): void {
    if (this.state.hasProfile() || this.state.isLoading()) {
      return;
    }

    // The failure is recorded in the state; a header has nowhere to show it.
    this.loadProfile().subscribe({ error: () => undefined });
  }

  /**
   * Sends a new profile picture and, on success, adopts it without another round
   * trip: the file just uploaded *is* the picture, so it becomes the rendered
   * avatar and its new `version` is written onto the stored profile — no second
   * `GET user/profile`, no `GET user/avatar` for bytes already in hand.
   *
   * A file the client gate turns down never reaches the network. Either way the
   * message to show lands in `vm().avatarUploadError`, and the stream errors so
   * the caller can drop its preview.
   */
  uploadAvatar(file: File): Observable<UserAvatar> {
    const rejection = this.rejectionFor(file);
    if (rejection) {
      const message = AVATAR_REJECTION_MESSAGE_KEY[rejection];
      this.state.setAvatarUploadError(message);

      return throwError(() => new Error(message));
    }

    this.state.setAvatarUploading();

    return this.api.uploadAvatar(file).pipe(
      take(1),
      tap({
        next: (avatar) => {
          // The file just uploaded *is* the picture, so a download still in
          // flight for the old one is moot — its `finalize` guard no longer
          // matches, so the flag is dropped here instead.
          this.pendingAvatarVersion = null;
          this.state.clearAvatarDownload();
          this.state.setAvatar(file, avatar.version);
          this.state.setProfileAvatar(avatar);
          this.state.setAvatarUploaded();
        },
        error: (error: unknown) => this.state.setAvatarUploadError(this.extractAvatarError(error)),
      }),
      finalize(() => {
        // A caller unsubscribing mid-flight (the account page being left) cancels
        // the request with neither handler running; the status cannot stay stuck
        // on `uploading`, or the avatar button would never unlock again.
        if (this.state.avatarUploadStatus() === 'uploading') {
          this.state.clearAvatarUpload();
        }
      }),
    );
  }

  /**
   * Drops the profile picture through `DELETE user/avatar` and, on success,
   * forgets it locally: the object URL being rendered is revoked and the stored
   * profile records `avatar: null`. That pair is what makes this page and the
   * shell header fall back to the initial badge, with no second
   * `GET user/profile` to learn what the request already settled.
   *
   * The route answers `200` even when the account had no picture, so a removal
   * with nothing to remove is a success. A refusal lands in
   * `vm().avatarRemoveError` and the stream errors, so the caller can toast it.
   */
  removeAvatar(): Observable<void> {
    this.state.setAvatarRemoving();

    return this.api.removeAvatar().pipe(
      take(1),
      tap({
        next: () => {
          // A download still in flight for the dropped picture must not land on
          // top of the cleared state; `clearProfileAvatar` also makes its guard
          // fail, since the version it compares against is gone.
          this.pendingAvatarVersion = null;
          this.state.clearAvatarDownload();
          this.state.clearAvatar();
          this.state.clearProfileAvatar();
          this.state.setAvatarRemoved();
        },
        error: (error: unknown) =>
          this.state.setAvatarRemoveError(
            this.extractErrorMessage(error, 'MY_ACCOUNT.PHOTO_REMOVE_ERROR'),
          ),
      }),
      finalize(() => {
        // A caller unsubscribing mid-flight (the account page being left) cancels
        // the request with neither handler running; the status cannot stay stuck
        // on `removing`, or the trash button would never unlock again.
        if (this.state.avatarRemoveStatus() === 'removing') {
          this.state.clearAvatarRemoval();
        }
      }),
    );
  }

  /**
   * Sends the new password to `PATCH user/password`.
   *
   * Nothing is checked here that the API does not check itself: `current_password`
   * is only known to be right by the server, so a refusal comes back as a `422`
   * on the offending field and lands — worded by the API — in
   * `vm().passwordFieldErrors`, next to the input that caused it. The message
   * with no field to sit under lands in `vm().passwordError` for the caller to
   * toast, and the stream errors so the page keeps what the user typed.
   */
  changePassword(payload: PasswordChangeRequest): Observable<PasswordChangeResult> {
    this.state.setPasswordSaving();

    return this.api.changePassword(payload).pipe(
      take(1),
      tap({
        next: () => this.state.setPasswordSaved(),
        error: (error: unknown) => this.recordPasswordFailure(error),
      }),
      finalize(() => {
        // A caller unsubscribing mid-flight (the account page being left) cancels
        // the request with neither handler running; the status cannot stay stuck
        // on `saving`, or the save button would never unlock again.
        if (this.state.passwordStatus() === 'saving') {
          this.state.clearPasswordChange();
        }
      }),
    );
  }

  /** Drops the messages of the last refused change — the user is editing again. */
  clearPasswordChange(): void {
    this.state.clearPasswordChange();
  }

  /**
   * Client gate applied before the upload: the type has to be one the API takes
   * (SVG is out — it is XML, and XML runs script) and the file has to fit under
   * the 5 MB ceiling. A file with no MIME at all defers to the server.
   *
   * The 64x64 minimum is left to the API: reading it means decoding the image,
   * and the `422` already comes back with a message ready to display.
   */
  rejectionFor(file: File): AvatarRejectionReason | null {
    const mime = file.type.trim().toLowerCase();
    if (mime && !AVATAR_ACCEPTED_MIME_TYPES.has(mime)) {
      return 'type';
    }

    return file.size > AVATAR_MAX_BYTES ? 'size' : null;
  }

  /** Clears the profile state (used when leaving the page / on logout). */
  reset(): void {
    // Dropped alongside the state: left behind, it would make the next session
    // mistake the picture it is downloading for one it already holds.
    this.pendingAvatarVersion = null;
    this.state.reset();
  }

  /**
   * Downloads the picture only when the account has one and the copy in state is
   * older than the version the profile reports. `avatar: null` means no picture:
   * the route is not called and the page draws the initial badge — which is also
   * what a failed download falls back to, since the picture is decoration.
   */
  private syncAvatar(avatar: UserAvatar | null): void {
    if (!avatar) {
      this.pendingAvatarVersion = null;
      this.state.clearAvatarDownload();
      this.state.clearAvatar();
      return;
    }

    if (
      this.state.avatarVersion() === avatar.version ||
      this.pendingAvatarVersion === avatar.version
    ) {
      return;
    }

    this.pendingAvatarVersion = avatar.version;
    this.state.setAvatarDownloading();

    this.api
      .getAvatar()
      .pipe(
        take(1),
        finalize(() => {
          // Only when this download is still the current one: a newer version
          // started meanwhile owns the flag, and clearing it here would drop the
          // bone while its bytes are still on the way.
          if (this.pendingAvatarVersion === avatar.version) {
            this.pendingAvatarVersion = null;
            this.state.clearAvatarDownload();
          }
        }),
      )
      .subscribe({
        next: (blob) => {
          // A picture uploaded while this download was in flight is the newer
          // one; the stale bytes must not overwrite it.
          if (this.state.profile()?.avatar?.version === avatar.version) {
            this.state.setAvatar(blob, avatar.version);
          }
        },
        error: () => this.state.clearAvatar(),
      });
  }

  /**
   * Message for a refused upload. A `422` reports the reason under `photo` in a
   * form meant to be displayed as-is (not an image, too large, too small); any
   * other failure falls back to a generic key.
   */
  private extractAvatarError(error: unknown): string {
    const validation = this.apiErrors.parseValidationError(error, AVATAR_FIELD_KEYS);
    const photoMessage = validation?.fieldErrors['photo']?.[0];
    if (photoMessage) {
      return photoMessage;
    }

    return this.extractErrorMessage(error, 'MY_ACCOUNT.PHOTO_UPLOAD_ERROR');
  }

  /**
   * Splits a refused change into what belongs under each input and what belongs
   * in a toast. Only the first message per field is kept: the inputs have room
   * for one line, and the API lists them in order of relevance.
   */
  private recordPasswordFailure(error: unknown): void {
    const validation = this.apiErrors.parseValidationError(error, PASSWORD_FIELD_KEYS);

    if (!validation) {
      this.state.setPasswordError(
        this.extractErrorMessage(error, 'MY_ACCOUNT.PASSWORD_SAVE_ERROR'),
      );
      return;
    }

    const fieldErrors: Record<string, string> = {};
    for (const [field, messages] of Object.entries(validation.fieldErrors)) {
      const [first] = messages;
      if (first) {
        fieldErrors[field] = first;
      }
    }

    // Whatever is not tied to a field (an unexpected key, the API's own summary)
    // has nowhere to show inline, so it is what the page toasts.
    const [alert] = this.apiErrors.topAlertMessages(validation);
    this.state.setPasswordError(alert ?? 'MY_ACCOUNT.PASSWORD_SAVE_ERROR', fieldErrors);
  }

  /**
   * Pulls the human-readable message out of the API error body, falling back to
   * a generic key when the response doesn't carry one.
   */
  private extractErrorMessage(error: unknown, fallback = 'MY_ACCOUNT.LOAD_ERROR'): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as ApiErrorBody | null;
      if (body?.message) {
        return body.message;
      }
    }

    return fallback;
  }
}
